import React from 'react';
import { Link } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';
import { useSupabaseData } from '../../contexts/SupabaseDataContext';

const AdminDashboard: React.FC = () => {
  const { inquiries, reviews, orders, isLoading: adminLoading } = useAdmin();
  const { products: supabaseProducts, isLoading: supabaseLoading } = useSupabaseData();

  const isLoading = adminLoading || supabaseLoading;

  const stats = {
    totalProducts: supabaseProducts.length,
    totalOrders: orders.length,
    pendingPayment: orders.filter(o => o.status === 'payment_pending').length,
    confirmed: orders.filter(o => o.status === 'payment_confirmed').length,
    shipping: orders.filter(o => o.status === 'shipping').length,
    delivered: orders.filter(o => o.status === 'delivered').length,
    pendingInquiries: inquiries.filter(i => i.status === 'pending').length,
    pendingReviews: reviews.filter(r => (r.status || (r.is_verified ? 'approved' : 'pending')) === 'pending').length,
    revenue: orders
      .filter(o => o.paymentStatus === 'paid' || o.status === 'payment_confirmed' || o.status === 'shipping' || o.status === 'delivered')
      .reduce((sum, o) => sum + (o.totalAmount || 0), 0),
  };

  const needsAttention = stats.pendingPayment + stats.confirmed + stats.pendingInquiries + stats.pendingReviews;

  const recentOrders = [...orders]
    .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
    .slice(0, 5);

  const recentInquiries = [...inquiries]
    .filter(i => i.status === 'pending')
    .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())
    .slice(0, 3);

  if (isLoading) {
    return (
      <div className="space-y-5">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-white/80 border border-warm-200/60 rounded-xl animate-pulse h-28" />
        ))}
      </div>
    );
  }

  const statusColor = (status: string) => {
    switch (status) {
      case 'payment_pending': return 'text-amber-600 bg-amber-50 border-amber-200/60';
      case 'payment_confirmed': return 'text-blue-600 bg-blue-50 border-blue-200/60';
      case 'shipping': return 'text-indigo-600 bg-indigo-50 border-indigo-200/60';
      case 'delivered': return 'text-emerald-600 bg-emerald-50 border-emerald-200/60';
      case 'cancelled': return 'text-warm-500 bg-warm-50 border-warm-200/60';
      default: return 'text-warm-500 bg-warm-50 border-warm-200/60';
    }
  };

  const statusLabel = (status: string) => {
    switch (status) {
      case 'payment_pending': return 'Awaiting Payment';
      case 'payment_confirmed': return 'Confirmed';
      case 'shipping': return 'Shipping';
      case 'delivered': return 'Delivered';
      case 'cancelled': return 'Cancelled';
      case 'refunded': return 'Refunded';
      default: return status;
    }
  };

  return (
    <div className="space-y-6">
      {/* Attention banner */}
      {needsAttention > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 bg-gold-50/80 border border-gold-200/70 rounded-xl">
          <div className="w-8 h-8 rounded-full bg-gold-100 border border-gold-200/60 flex items-center justify-center flex-shrink-0">
            <span className="text-[13px] font-bold text-gold-700">{needsAttention}</span>
          </div>
          <p className="text-[12.5px] text-gold-800 font-medium">
            {needsAttention} item{needsAttention !== 1 ? 's' : ''} need your attention
            <span className="text-gold-600 font-normal">
              {stats.pendingPayment > 0 && ` \u00b7 ${stats.pendingPayment} awaiting payment`}
              {stats.confirmed > 0 && ` \u00b7 ${stats.confirmed} ready to ship`}
              {stats.pendingInquiries > 0 && ` \u00b7 ${stats.pendingInquiries} unanswered inquiries`}
              {stats.pendingReviews > 0 && ` \u00b7 ${stats.pendingReviews} reviews to moderate`}
            </span>
          </p>
        </div>
      )}

      {/* Order pipeline */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Awaiting Payment', value: stats.pendingPayment, icon: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z', accent: 'text-amber-600 bg-amber-50 border-amber-200/60', href: '/admin/orders' },
          { label: 'Ready to Ship', value: stats.confirmed, icon: 'M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z', accent: 'text-blue-600 bg-blue-50 border-blue-200/60', href: '/admin/orders' },
          { label: 'In Transit', value: stats.shipping, icon: 'M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-1.5l-1.5-3h-6.75M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z', accent: 'text-indigo-600 bg-indigo-50 border-indigo-200/60', href: '/admin/orders' },
          { label: 'Delivered', value: stats.delivered, icon: 'M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0118 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3l1.5 1.5 3-3.75', accent: 'text-emerald-600 bg-emerald-50 border-emerald-200/60', href: '/admin/orders' },
        ].map((item) => (
          <Link
            key={item.label}
            to={item.href}
            className={`relative border rounded-xl p-4 transition-all duration-200 hover:shadow-sm ${item.accent}`}
          >
            <div className="flex items-center gap-2.5 mb-2">
              <svg className="w-[15px] h-[15px] opacity-60" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
              </svg>
              <span className="text-[10px] font-semibold uppercase tracking-[0.1em] opacity-70">{item.label}</span>
            </div>
            <div className="text-[28px] font-bold tracking-tight leading-none">{item.value}</div>
          </Link>
        ))}
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white/80 border border-warm-200/60 rounded-xl px-5 py-4">
          <div className="text-[10px] font-semibold text-warm-400 uppercase tracking-[0.1em] mb-1">Revenue</div>
          <div className="text-[22px] font-bold text-forest-900 tracking-tight">${stats.revenue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <div className="text-[11px] text-warm-400 mt-0.5">from {orders.filter(o => o.paymentStatus === 'paid' || o.status === 'payment_confirmed' || o.status === 'shipping' || o.status === 'delivered').length} paid orders</div>
        </div>
        <div className="bg-white/80 border border-warm-200/60 rounded-xl px-5 py-4">
          <div className="text-[10px] font-semibold text-warm-400 uppercase tracking-[0.1em] mb-1">Products</div>
          <div className="text-[22px] font-bold text-forest-900 tracking-tight">{stats.totalProducts}</div>
          <div className="text-[11px] text-warm-400 mt-0.5">active in catalog</div>
        </div>
        <div className="bg-white/80 border border-warm-200/60 rounded-xl px-5 py-4">
          <div className="text-[10px] font-semibold text-warm-400 uppercase tracking-[0.1em] mb-1">Total Orders</div>
          <div className="text-[22px] font-bold text-forest-900 tracking-tight">{stats.totalOrders}</div>
          <div className="text-[11px] text-warm-400 mt-0.5">all time</div>
        </div>
      </div>

      {/* Two columns: Recent Orders + Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Recent Orders - wider */}
        <div className="lg:col-span-3 bg-white/80 border border-warm-200/60 rounded-xl">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-warm-200/50">
            <h2 className="text-[13px] font-semibold text-forest-900">Recent Orders</h2>
            <Link to="/admin/orders" className="text-[11px] font-medium text-forest-600 hover:text-forest-800 transition-colors">
              View all
            </Link>
          </div>
          <div className="divide-y divide-warm-100/80">
            {recentOrders.length > 0 ? recentOrders.map((order) => (
              <div key={order.id} className="flex items-center justify-between px-5 py-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[12.5px] font-semibold text-forest-900">{order.orderNumber}</span>
                    <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full border ${statusColor(order.status)}`}>
                      {statusLabel(order.status)}
                    </span>
                  </div>
                  <div className="text-[11px] text-warm-400 mt-0.5">
                    {order.userName || 'Guest'} &middot; {new Date(order.createdAt || 0).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                </div>
                <div className="text-[13px] font-bold text-forest-900 tabular-nums">
                  ${(order.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
              </div>
            )) : (
              <div className="px-5 py-8 text-center">
                <svg className="w-8 h-8 text-warm-300 mx-auto mb-2" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                </svg>
                <p className="text-[12px] text-warm-400">No orders yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Right column: Quick nav + Pending inquiries */}
        <div className="lg:col-span-2 space-y-5">
          {/* Quick nav */}
          <div className="bg-white/80 border border-warm-200/60 rounded-xl">
            <div className="px-5 py-3.5 border-b border-warm-200/50">
              <h2 className="text-[13px] font-semibold text-forest-900">Quick Actions</h2>
            </div>
            <div className="p-3 grid grid-cols-2 gap-2">
              {[
                { label: 'Orders', href: '/admin/orders', icon: 'M15.75 10.5V6a3.75 3.75 0 10-7.5 0v4.5m11.356-1.993l1.263 12c.07.665-.45 1.243-1.119 1.243H4.25a1.125 1.125 0 01-1.12-1.243l1.264-12A1.125 1.125 0 015.513 7.5h12.974c.576 0 1.059.435 1.119 1.007zM8.625 10.5a.375.375 0 11-.75 0 .375.375 0 01.75 0zm7.5 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z' },
                { label: 'Products', href: '/admin/products', icon: 'M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21' },
                { label: 'Inquiries', href: '/admin/inquiries', icon: 'M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z', badge: stats.pendingInquiries },
                { label: 'Reviews', href: '/admin/reviews', icon: 'M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z', badge: stats.pendingReviews },
                { label: 'Coupons', href: '/admin/coupons', icon: 'M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z' },
                { label: 'Add Product', href: '/admin/products/new', icon: 'M12 4.5v15m7.5-7.5h-15' },
              ].map((item) => (
                <Link
                  key={item.label}
                  to={item.href}
                  className="relative flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-warm-600 hover:text-forest-800 hover:bg-forest-50/60 transition-all duration-200"
                >
                  <svg className="w-[15px] h-[15px] text-warm-400" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                  </svg>
                  <span className="text-[12px] font-medium">{item.label}</span>
                  {'badge' in item && (item as any).badge > 0 && (
                    <span className="ml-auto text-[10px] font-bold text-gold-700 bg-gold-100 border border-gold-200/60 rounded-full px-1.5 py-0.5 leading-none">
                      {(item as any).badge}
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </div>

          {/* Pending inquiries */}
          <div className="bg-white/80 border border-warm-200/60 rounded-xl">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-warm-200/50">
              <h2 className="text-[13px] font-semibold text-forest-900">Unanswered Inquiries</h2>
              <Link to="/admin/inquiries" className="text-[11px] font-medium text-forest-600 hover:text-forest-800 transition-colors">
                View all
              </Link>
            </div>
            <div className="divide-y divide-warm-100/80">
              {recentInquiries.length > 0 ? recentInquiries.map((inq) => (
                <div key={inq.id} className="px-5 py-3">
                  <div className="text-[12.5px] font-medium text-forest-900 truncate">{inq.title}</div>
                  <div className="text-[11px] text-warm-400 mt-0.5">
                    {(inq as any).userName || (inq as any).user_name || 'Anonymous'} &middot; {new Date(inq.createdAt || 0).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                </div>
              )) : (
                <div className="px-5 py-6 text-center">
                  <p className="text-[12px] text-warm-400">All caught up</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;